// ============================================================
// Privacy configuration for the marketing site (DPDP Act 2023).
// Everything a lawyer or the business may need to change lives here:
// the notice version, the Grievance Officer, retention periods, the
// consent categories and the cookie / storage inventory.
//
// Items marked TO BE CONFIRMED are proposals written for review; they
// must be confirmed by Raya (and counsel) before this goes live.
// ============================================================
import { CONTACT_EMAIL, LEGAL_ENTITY_NAME, REGISTERED_ADDRESS } from '../constants/constants';

export const NOTICE_VERSION = '2.0';
export const NOTICE_LAST_UPDATED = '20 October 2026';
export const NOTICE_EFFECTIVE_FROM = '20 October 2026';

// Bump CONSENT_VERSION when the categories or what they load change: every
// visitor is then asked again. Choices older than CONSENT_MAX_AGE_DAYS are
// asked again too.
export const CONSENT_VERSION = 1;
export const CONSENT_MAX_AGE_DAYS = 365;
export const CONSENT_STORAGE_KEY = 'raya.consent';

export const DATA_FIDUCIARY = {
  name: LEGAL_ENTITY_NAME,
  address: REGISTERED_ADDRESS,
  email: CONTACT_EMAIL,
};

// TO BE CONFIRMED by Raya: proposed retention for website data. Platform
// figures (7 years for financial records, 30 days after cancellation) are
// the ones already published in the Terms.
export const RETENTION = {
  enquiry: 'up to 24 months after our last conversation, unless you become a customer',
  signup: 'for as long as your account is open, then as described for customer accounts',
  privacyRequest: '3 years after we close the request, to show how we handled it',
  consentRecord: 'for as long as the choice applies, plus 3 years',
  financial: '7 years, to meet GST and company-law obligations',
  afterCancellation: '30 days after cancellation, so you can export your data',
  technicalLogs: 'only as long as needed to investigate and fix problems, and no longer than 12 months',
};

export const CONSENT_CATEGORIES = [
  {
    key: 'necessary',
    label: 'Strictly necessary',
    required: true,
    desc: 'Keeps you signed in after you log in, protects the session and remembers the privacy choices on this page. The site cannot work without these, so they cannot be switched off.',
  },
  {
    key: 'security',
    label: 'Form protection (Google reCAPTCHA)',
    desc: 'Loads Google reCAPTCHA on the sign-up and password-reset forms to stop automated abuse. Google receives your IP address, browser details and how you use the page, and may set its own cookie.',
  },
  {
    key: 'analytics',
    label: 'Analytics',
    desc: 'Would help us understand which pages are useful. Nothing in this category is in use today; if we add a tool, it will load only for visitors who switch this on.',
  },
  {
    key: 'marketing',
    label: 'Marketing',
    desc: 'Would measure advertising. Nothing in this category is in use today, and we do not sell or share audience data.',
  },
];

export const OPTIONAL_CATEGORIES = CONSENT_CATEGORIES.filter((c) => !c.required).map((c) => c.key);

// Every cookie and browser-storage item this website can set.
export const COOKIES = [
  { name: CONSENT_STORAGE_KEY, kind: 'Local storage', by: 'Raya', category: 'Strictly necessary', purpose: 'Your privacy choices, the date you made them and the notice version.', duration: `${CONSENT_MAX_AGE_DAYS} days, or until you change them` },
  { name: 'authToken', kind: 'Cookie and local storage', by: 'Raya', category: 'Strictly necessary', purpose: 'Keeps you signed in after you log in or sign up, so the app opens without asking again.', duration: 'Until you log out or the session expires' },
  { name: 'raya.recentAccounts', kind: 'Local storage', by: 'Raya', category: 'Strictly necessary', purpose: 'Remembers the company accounts you recently signed in to on this device, on the log-in page only.', duration: 'Until you clear it' },
  { name: '_GRECAPTCHA', kind: 'Cookie', by: 'Google (reCAPTCHA)', category: 'Form protection', purpose: 'Set by Google when reCAPTCHA loads on the sign-up or password-reset form, to tell people from bots.', duration: 'Up to 6 months (set by Google)' },
];
